import React, { useState, useRef, useEffect } from "react";
import { useTheme } from '../../contexts/ThemeContext';

const ITEMS_PER_PAGE = 10;

// Helper functions for date filter labels
const getTypeLabel = (type) => {
  const labels = {
    today: 'Сегодня',
    yesterday: 'Вчера',
    week: 'За неделю',
    month: 'За месяц',
  };
  return labels[type] || type;
};

const getDateLabel = (type) => {
  return getTypeLabel(type);
};

export default function ModerationHeader({
  status,
  totalItems,
  totalPages,
  page,
  announcementsLength,
  selectAll,
  onSelectAll,
  selectedCount = 0,
  onSelectAllMatching,
  selectingAll = false,
  categories = [],
  selectedCategory,
  onCategoryChange,
  subcategories = [],
  selectedSubcategory,
  onSubcategoryChange,
  regions = [],
  selectedRegion,
  onRegionChange,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  dateFilter,
  onDateFilterChange,
  photosFilter,
  onPhotosFilterChange,
}) {
  const [showDatePopup, setShowDatePopup] = useState(false);
  const [tempDateFilter, setTempDateFilter] = useState(dateFilter || { type: null, from: '', to: '' });
  const dateButtonRef = useRef(null);

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateButtonRef.current && !dateButtonRef.current.contains(event.target)) {
        setShowDatePopup(false);
      }
    };

    if (showDatePopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDatePopup]);
  const getTitle = () => {
    switch (status) {
      case "pending": return totalItems != null ? `В ожидании ${totalItems.toLocaleString("ru-RU")}` : "В ожидании";
      case "active": return totalItems != null ? `Принятые ${totalItems.toLocaleString("ru-RU")}` : "Принятые";
      case "rejected": return totalItems != null ? `Отклоненные ${totalItems.toLocaleString("ru-RU")}` : "Отклоненные";
      default: return totalItems != null ? `Объявления ${totalItems.toLocaleString("ru-RU")}` : "Объявления";
    }
  };

  const getPageInfo = () => {
    if (totalItems != null && totalPages > 1) {
      return `страница ${page} из ${totalPages}`;
    }
    return null;
  };

  return (
    <div className="dark-card moderation-header p-6 animate-fade-in-up">
      {/* Top Row: Status, Count, Description, Select All */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{getTitle()}</h1>
          {getPageInfo() && <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{getPageInfo()}</p>}
        </div>

        {/* Select All */}
        {announcementsLength > 0 && (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={onSelectAll}
              className="w-5 h-5 accent-[#4F45E5] border-gray-600 rounded focus:ring-[#4F45E5] bg-gray-800"
            />
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Выбрать все</span>
            {/* Беттегілер таңдалса — сүзгі бойынша барлық беттерді таңдау */}
            {selectAll && onSelectAllMatching && totalItems > selectedCount && (
              <button
                type="button"
                onClick={onSelectAllMatching}
                disabled={selectingAll}
                className="text-sm font-medium text-[#4F45E5] hover:underline disabled:opacity-60"
              >
                {selectingAll ? "Загрузка…" : `Выбрать все ${totalItems}`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bottom Row: Filters + Sort */}
      <div className="flex flex-wrap items-center gap-3" style={{ overflow: 'visible' }}>
        {/* Filters Group */}
        <div className="flex flex-wrap items-center gap-3" style={{ overflow: 'visible' }}>
          {/* Category Dropdown */}
          {categories.length > 0 && (
            <div className="relative">
              <select
                value={selectedCategory || ""}
                onChange={(e) => onCategoryChange(e.target.value ? Number(e.target.value) : null)}
                className={`pl-4 pr-10 py-2 border rounded-lg text-sm cursor-pointer appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F45E5] focus:border-[#4F45E5] ${isDark ? 'bg-[#222630] border-[#333333] text-white hover:border-gray-500' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'}`}
              >
                <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} value="">Категория</option>
                {categories.map((cat) => (
                  <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} key={cat.id} value={cat.id}>{cat.name_kz || cat.name_ru || cat.name}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          )}

          {/* Subcategory Dropdown */}
          {subcategories.length > 0 && (
            <div className="relative">
              <select
                value={selectedSubcategory || ""}
                onChange={(e) => onSubcategoryChange(e.target.value ? Number(e.target.value) : null)}
                className={`pl-4 pr-10 py-2 border rounded-lg text-sm cursor-pointer appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F45E5] focus:border-[#4F45E5] ${isDark ? 'bg-[#222630] border-[#333333] text-white hover:border-gray-500' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'}`}
              >
                <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} value="">Подкатегория</option>
                {subcategories.map((sub) => (
                  <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} key={sub.id} value={sub.id}>{sub.name_kz || sub.name_ru || sub.name}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          )}

          {/* Region Dropdown */}
          {regions.length > 0 && (
            <div className="relative">
              <select
                value={selectedRegion || ""}
                onChange={(e) => onRegionChange(e.target.value ? Number(e.target.value) : null)}
                className={`pl-4 pr-10 py-2 border rounded-lg text-sm cursor-pointer appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F45E5] focus:border-[#4F45E5] ${isDark ? 'bg-[#222630] border-[#333333] text-white hover:border-gray-500' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'}`}
              >
                <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} value="">Регион</option>
                {regions.map((reg) => (
                  <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} key={reg.id} value={reg.id}>{reg.name_ru || reg.name_kz}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          )}

          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery || ""}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Название, #ID, продавец или телефон"
              className={`pl-10 pr-4 py-2 border rounded-lg text-sm placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F45E5] focus:border-[#4F45E5] w-72 ${isDark ? 'bg-[#222630] border-[#333333] text-white hover:border-gray-500' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'}`}
            />
          </div>

          {/* Date Filter Button */}
          <div ref={dateButtonRef} className="relative">
            <button
              onClick={() => {
                setTempDateFilter(dateFilter || { type: null, from: '', to: '' });
                setShowDatePopup(!showDatePopup);
              }}
              className={`flex items-center gap-2 pl-4 pr-4 py-2 rounded-lg text-sm font-medium border ${
                dateFilter?.type
                  ? "bg-accent-grad text-white border-transparent"
                  : isDark
                    ? "bg-transparent text-gray-300 border-[#333333]"
                    : "bg-transparent text-gray-600 border-gray-300"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{dateFilter?.type ? getDateLabel(dateFilter.type) : "Дата"}</span>
            </button>

            {/* Date Filter Popup */}
            {showDatePopup && (
              <div
                className={`fixed w-80 rounded-xl shadow-2xl z-[100] animate-fade-in-up ${isDark ? 'bg-[#1C2127] border border-[#333333]' : 'bg-white border border-gray-200'}`}
                style={{
                  top: `${(dateButtonRef.current?.getBoundingClientRect().bottom || 0) + 8}px`,
                  right: `${window.innerWidth - (dateButtonRef.current?.getBoundingClientRect().right || 0)}px`,
                }}
              >
                  {/* Quick Select Buttons */}
                  <div className={`p-4 border-b ${isDark ? 'border-[#333333]' : 'border-gray-200'}`}>
                    <div className="grid grid-cols-4 gap-2">
                      {['today', 'yesterday', 'week', 'month'].map((type) => (
                        <button
                          key={type}
                          onClick={() => setTempDateFilter({ ...tempDateFilter, type })}
                          className={`py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                            tempDateFilter.type === type
                              ? "bg-accent-grad text-white"
                              : isDark
                                ? "bg-[#2a2f36] text-gray-400 hover:text-white"
                                : "bg-gray-100 text-gray-600 hover:text-gray-900"
                          }`}
                        >
                          {getTypeLabel(type)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className={`h-px mx-4 ${isDark ? 'bg-[#333333]' : 'bg-gray-200'}`} />

                  {/* Date Range Inputs with Calendar */}
                  <div className={`p-4 border-b ${isDark ? 'border-[#333333]' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 flex items-center gap-2">
                        <span className={`text-xs whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>С</span>
                        <input
                          type="date"
                          value={tempDateFilter.from}
                          onChange={(e) => setTempDateFilter({ ...tempDateFilter, from: e.target.value })}
                          className={`flex-1 px-3 py-1.5 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4F45E5] ${isDark ? 'bg-[#2a2f36] border border-[#333333] text-white' : 'bg-white border border-gray-300 text-gray-900'}`}
                        />
                      </div>
                      <div className="flex-1 flex items-center gap-2">
                        <span className={`text-xs whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>По</span>
                        <input
                          type="date"
                          value={tempDateFilter.to}
                          onChange={(e) => setTempDateFilter({ ...tempDateFilter, to: e.target.value })}
                          className={`flex-1 px-3 py-1.5 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4F45E5] ${isDark ? 'bg-[#2a2f36] border border-[#333333] text-white' : 'bg-white border border-gray-300 text-gray-900'}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className={`h-px mx-4 ${isDark ? 'bg-[#333333]' : 'bg-gray-200'}`} />

                  {/* Action Buttons */}
                  <div className="p-4 flex items-center justify-between gap-3">
                    <button
                      onClick={() => {
                        setTempDateFilter({ type: null, from: '', to: '' });
                        onDateFilterChange(null);
                        setShowDatePopup(false);
                      }}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${isDark ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-500'}`}
                    >
                      Сбросить
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowDatePopup(false)}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                      >
                        Отмена
                      </button>
                      <button
                        onClick={() => {
                          onDateFilterChange(tempDateFilter);
                          setShowDatePopup(false);
                        }}
                        className="px-4 py-2 text-sm font-medium text-white bg-accent-grad rounded-lg hover:opacity-90 transition-opacity"
                      >
                        Применить
                      </button>
                    </div>
                  </div>
                </div>
            )}
          </div>

          {/* Photos Filter Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPhotosFilterChange(null)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
                photosFilter === null
                  ? "bg-accent-grad text-white border-transparent"
                  : isDark
                    ? "bg-transparent text-gray-300 border-[#333333]"
                    : "bg-transparent text-gray-600 border-gray-300"
              }`}
            >
              Все
            </button>
            <button
              onClick={() => onPhotosFilterChange(true)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border flex items-center gap-2 ${
                photosFilter === true
                  ? "bg-accent-grad text-white border-transparent"
                  : isDark
                    ? "bg-transparent text-gray-300 border-[#333333]"
                    : "bg-transparent text-gray-600 border-gray-300"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              С фото
            </button>
            <button
              onClick={() => onPhotosFilterChange(false)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border flex items-center gap-2 ${
                photosFilter === false
                  ? "bg-accent-grad text-white border-transparent"
                  : isDark
                    ? "bg-transparent text-gray-300 border-[#333333]"
                    : "bg-transparent text-gray-600 border-gray-300"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l12 12" />
              </svg>
              Без фото
            </button>
          </div>
        </div>

        {/* Sort Dropdown - separated with margin */}
        <div className="relative ml-auto">
          <select
            value={sortBy || "newest"}
            onChange={(e) => onSortChange(e.target.value)}
            className={`pl-4 pr-10 py-2 border rounded-lg text-sm cursor-pointer appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F45E5] focus:border-[#4F45E5] ${isDark ? 'bg-[#222630] border-[#333333] text-white hover:border-gray-500' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'}`}
          >
            <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} value="newest">Сначала новые</option>
            <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} value="oldest">Сначала старые</option>
            <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} value="price_asc">По цене ↑</option>
            <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} value="price_desc">По цене ↓</option>
            <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} value="name_asc">По названию (А-Я)</option>
            <option className={isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} value="name_desc">По названию (Я-А)</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

export { ITEMS_PER_PAGE };